import { Button } from "@/components/ui/button";
import { Play, BarChart3, Mail, Download, Instagram, Film } from "lucide-react";
import { athlete, contact, playerProfiles } from "@/data/athlete";

const hudl = playerProfiles.find((p) => p.name === "Hudl");
const maxpreps = playerProfiles.find((p) => p.name === "MaxPreps");

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${athlete.heroBackground})` }}
        aria-hidden
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#0A0A0A]"
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-14 pb-16 md:pt-20 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          {/* Left: Name + meta */}
          <div className="lg:col-span-8">
            <div
              data-testid="hero-meta"
              className="flex flex-wrap items-center gap-3 mb-6"
            >
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF3B30]">
                Class of {athlete.gradYear}
              </span>
              <span className="text-zinc-600">/</span>
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-400">
                {athlete.position}
              </span>
              <span className="text-zinc-600">/</span>
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-400">
                #{athlete.jerseyNumber}
              </span>
            </div>

            <h1
              data-testid="hero-name"
              className="font-display text-6xl sm:text-8xl md:text-9xl uppercase leading-[0.85] tracking-tighter text-white"
            >
              {athlete.firstName}
              <br />
              <span className="text-[#FF3B30]">{athlete.lastName}</span>
            </h1>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-zinc-400">
              <span data-testid="hero-stat-height">
                <span className="text-zinc-600 mr-2">HT</span>
                <span className="text-white font-semibold">
                  {athlete.height}
                </span>
              </span>
              <span data-testid="hero-stat-weight">
                <span className="text-zinc-600 mr-2">WT</span>
                <span className="text-white font-semibold">
                  {athlete.weight}
                </span>
              </span>
              <span data-testid="hero-stat-hometown">
                <span className="text-zinc-600 mr-2">FROM</span>
                <span className="text-white font-semibold">
                  {athlete.hometown}
                </span>
              </span>
            </div>

            <p
              data-testid="hero-jersey-support"
              className="mt-3 text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500"
            >
              Also worn: #{athlete.jerseyNumberHS} (HS), #
              {athlete.jerseyNumberAAU} (AAU)
            </p>

            <p
              data-testid="hero-headline"
              className="mt-8 max-w-2xl text-xl md:text-2xl text-white font-semibold leading-snug"
            >
              {athlete.headline}
            </p>
            <p
              data-testid="hero-subline"
              className="mt-3 max-w-2xl text-sm md:text-base uppercase tracking-[0.18em] font-bold text-[#FF3B30]"
            >
              {athlete.subline}
            </p>
            <p
              data-testid="hero-honors"
              className="mt-2 max-w-2xl text-xs md:text-sm uppercase tracking-[0.18em] font-semibold text-zinc-400"
            >
              {athlete.honors}
            </p>

            {(contact.instagram || hudl || maxpreps) && (
              <div
                data-testid="hero-quick-links"
                className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm font-semibold tracking-[0.12em]"
              >
                {contact.instagram && (
                  <a
                    data-testid="hero-instagram"
                    href={`https://instagram.com/${contact.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 py-1 text-zinc-400 hover:text-[#FF3B30] transition-colors"
                  >
                    <Instagram className="h-4 w-4" />
                    @{contact.instagram}
                  </a>
                )}
                {hudl && (
                  <>
                    <span className="text-zinc-700" aria-hidden>|</span>
                    <a
                      data-testid="hero-hudl"
                      href={hudl.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 py-1 text-zinc-400 hover:text-[#FF3B30] transition-colors"
                    >
                      <Film className="h-4 w-4" />
                      Hudl
                    </a>
                  </>
                )}
                {maxpreps && (
                  <>
                    <span className="text-zinc-700" aria-hidden>|</span>
                    <a
                      data-testid="hero-maxpreps"
                      href={maxpreps.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 py-1 text-zinc-400 hover:text-[#FF3B30] transition-colors"
                    >
                      <BarChart3 className="h-4 w-4" />
                      MaxPreps
                    </a>
                  </>
                )}
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                data-testid="hero-watch-highlights-btn"
                onClick={() => scrollTo("film")}
                className="rounded-sm bg-[#FF3B30] hover:bg-[#FF5147] text-white font-bold uppercase tracking-wider px-6 h-12"
              >
                <Play className="mr-2 h-4 w-4 fill-white" />
                Watch Highlights
              </Button>
              <Button
                data-testid="hero-view-stats-btn"
                onClick={() => scrollTo("stats")}
                variant="outline"
                className="rounded-sm border-zinc-700 bg-transparent text-white hover:bg-white hover:text-black uppercase tracking-wider font-bold px-6 h-12"
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                View Stats
              </Button>
              <Button
                data-testid="hero-contact-btn"
                onClick={() => scrollTo("contact")}
                variant="outline"
                className="rounded-sm border-zinc-700 bg-transparent text-white hover:bg-white hover:text-black uppercase tracking-wider font-bold px-6 h-12"
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact
              </Button>
              <button
                type="button"
                data-testid="hero-download-pdf-btn"
                onClick={() => window.print()}
                className="inline-flex items-center justify-center rounded-sm border border-zinc-700 bg-transparent text-white hover:bg-white hover:text-black uppercase tracking-wider font-bold px-6 h-12 text-sm transition-colors no-print"
              >
                <Download className="mr-2 h-4 w-4" />
                Download Profile (PDF)
              </button>
            </div>
            <p
              data-testid="hero-helper-text"
              className="mt-4 text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500"
            >
              Coaches: Scroll for film or contact below
            </p>
          </div>

          {/* Right: Portrait */}
          <div className="lg:col-span-4">
            <div
              data-testid="hero-portrait"
              className="relative aspect-[3/4] w-full border border-zinc-800 overflow-hidden"
            >
              <img
                src={athlete.profileImage}
                alt={`${athlete.firstName} ${athlete.lastName}`}
                className="absolute inset-0 w-full h-full object-cover grayscale-[20%]"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-5">
                <div className="font-display text-5xl text-white leading-none">
                  #{athlete.jerseyNumber}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.25em] font-bold text-[#FF3B30]">
                  {athlete.positionShort} · {athlete.gradYear}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
